import React, { useEffect, useMemo, useState } from 'react';
import '../business/Business.css';

import DesktopOnlyBlock from '../business/components/DesktopOnlyBlock';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import CreateFooter from '../business/components/CreateFooter';
import useHttp from '../../hooks/http.hook';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useBranchNav } from '../../hooks/branchNav.hook';

import ProductDetailsForm from './components/ProductDetailsForm';
import ProductDurationField from './components/ProductDurationField';
import ProductVisibilityButton from './components/ProductVisibilityButton';
import { normalizeProductGroupsResponse } from '../../utils/productGroups';
import { ProductGroupStatusTypes } from '../../types/ProductGroupStatusTypes';
import { BusinessTypes } from '../../types/BusinessType';
import {
    IMAGE_LIMIT,
    normalizeProductImages,
    primaryProductImageUrl,
} from '../../utils/productImages';

function UpsertProduct({ onSaved }) {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const { productsPath } = useBranchNav();

    const productId = params.productId || null;
    const isEdit = !!productId;

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [images, setImages] = useState([]);
    const [price, setPrice] = useState('');
    const [durationMinutes, setDurationMinutes] = useState(null);
    const [businessTypeId, setBusinessTypeId] = useState(BusinessTypes.STORE);
    const [variantCount, setVariantCount] = useState(1);
    const [statusId, setStatusId] = useState(ProductGroupStatusTypes.VISIBLE);
    const [submitError, setSubmitError] = useState(null);
    const [isPrefillLoading, setIsPrefillLoading] = useState(false);
    const [isImageUploading, setIsImageUploading] = useState(false);

    const { loading, requestWithMeta } = useHttp();

    const parsedPrice = useMemo(() => {
        const normalized = String(price).replace(',', '.').trim();
        if (!normalized) return NaN;
        const n = Number(normalized);
        return Number.isFinite(n) ? n : NaN;
    }, [price]);

    const showPriceField = !isEdit || variantCount <= 1;
    const isRental = businessTypeId === BusinessTypes.RENTAL;
    const normalizedImages = useMemo(() => normalizeProductImages(images), [images]);

    const payloadPreview = useMemo(() => {
        return {
            name: name.trim(),
            description: String(description ?? '').trim() || null,
            images: normalizedImages,
            imageUrl: primaryProductImageUrl(normalizedImages),
            price: parsedPrice,
            ...(isRental && showPriceField ? { durationMinutes } : {}),
        };
    }, [name, description, normalizedImages, parsedPrice, isRental, showPriceField, durationMinutes]);

    function applyProductPrefill(product) {
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        setName(product?.name ?? '');
        setDescription(product?.description ?? '');
        setImages(
            normalizeProductImages(product?.images, product?.imageUrl ?? product?.image_url ?? null)
        );
        setVariantCount(variants.length || 1);
        const nextStatusId = Number(product?.statusId);
        setStatusId(
            nextStatusId === ProductGroupStatusTypes.INVISIBLE
                ? ProductGroupStatusTypes.INVISIBLE
                : ProductGroupStatusTypes.VISIBLE
        );
        const single = variants.length === 1 ? variants[0] : null;
        setPrice(
            single?.price !== undefined && single?.price !== null
                ? String(single.price)
                : product?.price !== undefined && product?.price !== null
                    ? String(product.price)
                    : ''
        );
        setDurationMinutes(single?.durationMinutes ?? null);
    }

    useEffect(() => {
        let cancelled = false;
        if (!isEdit) return;

        setSubmitError(null);

        async function prefill() {
            setIsPrefillLoading(true);
            try {
                const stateProduct = location?.state?.product ?? null;
                const stateId = stateProduct?._id ?? stateProduct?.id ?? null;
                if (stateProduct && stateId && String(stateId) === String(productId)) {
                    if (cancelled) return;
                    applyProductPrefill(stateProduct);
                    const stateStatusId = Number(stateProduct?.statusId);
                    if (
                        stateStatusId === ProductGroupStatusTypes.VISIBLE ||
                        stateStatusId === ProductGroupStatusTypes.INVISIBLE
                    ) {
                        return;
                    }
                }

                const res = await requestWithMeta('/operator/products', 'GET');
                if (!res?.ok) {
                    if (cancelled) return;
                    setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить товары');
                    return;
                }

                const listRaw = normalizeProductGroupsResponse(res?.data);
                const found = listRaw.find((p) => {
                    const id = p?._id ?? p?.id;
                    if (!id) return false;
                    return String(id) === String(productId);
                });

                if (!found) {
                    if (cancelled) return;
                    setSubmitError('Товар не найден');
                    return;
                }

                if (cancelled) return;
                applyProductPrefill(found);
            } finally {
                if (!cancelled) setIsPrefillLoading(false);
            }
        }

        prefill();
        return () => {
            cancelled = true;
        };
    }, [isEdit, productId, location?.state, requestWithMeta]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await requestWithMeta('/operator/business', 'GET');
            if (cancelled || !res?.ok) return;
            const business = res?.data?.business ?? res?.data ?? null;
            setBusinessTypeId(Number(business?.typeId) === BusinessTypes.RENTAL
                ? BusinessTypes.RENTAL
                : BusinessTypes.STORE);
        })();
        return () => {
            cancelled = true;
        };
    }, [requestWithMeta]);

    const isSubmitDisabled =
        loading ||
        isImageUploading ||
        isPrefillLoading ||
        name.trim().length === 0 ||
        (showPriceField && (!Number.isFinite(parsedPrice) || parsedPrice < 0)) ||
        (isRental && showPriceField && !durationMinutes);

    async function handleVisibilityToggle() {
        if (!isEdit || loading || isPrefillLoading) return;

        const nextStatusId =
            statusId === ProductGroupStatusTypes.VISIBLE
                ? ProductGroupStatusTypes.INVISIBLE
                : ProductGroupStatusTypes.VISIBLE;

        setSubmitError(null);
        const res = await requestWithMeta('/operator/products/status', 'PUT', {
            productId,
            statusId: nextStatusId,
        });

        if (!res?.ok) {
            setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось изменить видимость товара');
            return;
        }

        setStatusId(nextStatusId);
    }

    async function handleSubmit() {
        setSubmitError(null);

        if (normalizedImages.length > IMAGE_LIMIT) {
            setSubmitError(`Лимит изображений: ${IMAGE_LIMIT}. Сейчас ${normalizedImages.length}.`);
            return;
        }

        if (isEdit) {
            const body = {
                productId,
                name: String(name ?? '').trim(),
                description: String(description ?? '').trim() || null,
                images: normalizedImages,
                imageUrl: primaryProductImageUrl(normalizedImages),
            };

            if (showPriceField) {
                const priceNumber = Number(String(price).replace(',', '.').trim());
                if (!Number.isFinite(priceNumber) || priceNumber < 0) {
                    setSubmitError('Цена должна быть числом >= 0');
                    return;
                }
                body.price = priceNumber;
                if (isRental) {
                    if (!durationMinutes) {
                        setSubmitError('Укажите длительность аренды');
                        return;
                    }
                    body.durationMinutes = durationMinutes;
                }
            }

            const res = await requestWithMeta('/operator/products', 'PUT', body);

            if (!res?.ok) {
                setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось обновить товар');
                return;
            }

            const updatedId = res?.data?.product?.id ?? res?.data?.product?._id ?? productId;
            const updated = res?.data?.product
                ? { ...res.data.product, _id: updatedId, id: updatedId }
                : null;
            if (typeof onSaved === 'function') onSaved(updated ?? { _id: productId, id: productId });
            navigate(productsPath, { replace: true });
            return;
        }

        const res = await requestWithMeta('/operator/products', 'POST', payloadPreview);
        if (!res?.ok) {
            setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось создать товар');
            return;
        }

        setName('');
        setDescription('');
        setImages([]);
        setPrice('');

        if (typeof onSaved === 'function') onSaved(res?.data ?? null);
        navigate(productsPath, { replace: true });
    }

    return (
        <div className="shop-create-root">
            <DesktopOnlyBlock />

            <div className="shop-create-mobile">
                <CreateBusinessHeader
                    title={isEdit ? 'Редактировать товар' : 'Создать товар'}
                    subtitle={
                        isEdit
                            ? showPriceField
                                ? 'Измените название, описание, изображения и цену'
                                : 'Измените название, описание и изображения (варианты управляются в списке)'
                            : 'Добавьте название, описание, изображения и цену'
                    }
                    onBack={() => navigate(productsPath)}
                />

                <main className="shop-create-content">
                    {submitError ? (
                        <section className="shop-create-section shop-create-sectionError">
                            <div className="shop-create-label">Ошибка</div>
                            <div className="shop-create-help shop-create-helpTight">{submitError}</div>
                        </section>
                    ) : null}

                    <ProductDetailsForm
                        name={name}
                        onNameChange={setName}
                        description={description}
                        onDescriptionChange={setDescription}
                        images={images}
                        onImagesChange={setImages}
                        onImageUploadLoadingChange={setIsImageUploading}
                        price={price}
                        onPriceChange={setPrice}
                        hidePrice={!showPriceField}
                    />
                    {isRental && showPriceField ? (
                        <ProductDurationField
                            durationMinutes={durationMinutes}
                            onDurationMinutesChange={setDurationMinutes}
                            price={price}
                        />
                    ) : null}
                </main>

                <CreateFooter
                    disabled={isSubmitDisabled}
                    label={
                        loading
                            ? isEdit
                                ? 'Сохранение…'
                                : 'Создание…'
                            : isEdit
                                ? 'Сохранить'
                                : 'Создать товар'
                    }
                    onCreate={handleSubmit}
                    leading={
                        isEdit ? (
                            <ProductVisibilityButton
                                visible={statusId === ProductGroupStatusTypes.VISIBLE}
                                disabled={loading || isPrefillLoading}
                                onToggle={handleVisibilityToggle}
                            />
                        ) : null
                    }
                />
            </div>
        </div>
    );
}

export default UpsertProduct;
